import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getAdminOrganization, createOrganization, updateOrganization } from '../lib/api'

interface FormState {
  name: string
  slug: string
  logo_url: string
}

const blank: FormState = { name: '', slug: '', logo_url: '' }

function slugify(s: string) {
  return s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
}

export default function TenantFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const { token } = useAuth()
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState>(blank)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [slugManual, setSlugManual] = useState(false)

  useEffect(() => {
    if (isEdit && id && token) {
      getAdminOrganization(token, id)
        .then(org => {
          setForm({ name: org.name, slug: org.slug, logo_url: org.logo_url ?? '' })
          setSlugManual(true)
        })
        .catch(console.error)
        .finally(() => setLoading(false))
    }
  }, [isEdit, id, token])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm(prev => ({ ...prev, [k]: v }))

  const handleNameChange = (v: string) => {
    set('name', v)
    if (!slugManual) set('slug', slugify(v))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setSaving(true)
    setError('')
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        logo_url: form.logo_url.trim() || null,
      }
      if (isEdit && id) {
        await updateOrganization(token, id, payload)
        navigate(`/sales/tenants/${id}/overview`)
      } else {
        const created = await createOrganization(token, payload)
        navigate(`/sales/tenants/${created.id}/overview`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan.')
    } finally {
      setSaving(false)
    }
  }

  const label = 'block text-xs text-zinc-400 mb-1.5'
  const input = 'w-full bg-zinc-800 text-white rounded-lg px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600'

  if (loading) return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit}>
        <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors mb-3"
            >
              <ChevronLeft size={14} />
              Kembali
            </button>
            <h1 className="text-xl font-bold text-white">{isEdit ? 'Edit Tenant' : 'New Tenant'}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-2 text-sm text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 text-sm font-semibold bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Menyimpan…' : isEdit ? 'Simpan Perubahan' : 'Buat Tenant'}
            </button>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
            <div className="px-5 py-3 border-b border-zinc-800">
              <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Informasi Org</h2>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className={label}>Nama <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  className={input}
                  value={form.name}
                  onChange={e => handleNameChange(e.target.value)}
                  placeholder="Local Idol Culture"
                  required
                />
              </div>
              <div>
                <label className={label}>Slug <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  className={input}
                  value={form.slug}
                  onChange={e => { setSlugManual(true); set('slug', e.target.value) }}
                  placeholder="local-idol-culture"
                  pattern="[a-z0-9-]+"
                  title="Hanya huruf kecil, angka, dan tanda hubung"
                  required
                />
                <p className="text-[11px] text-zinc-600 mt-1">Hanya huruf kecil, angka, dan tanda hubung.</p>
              </div>
              <div>
                <label className={label}>
                  Logo URL{' '}
                  <span className="text-zinc-600 font-normal normal-case tracking-normal">— opsional</span>
                </label>
                <input
                  type="url"
                  className={input}
                  value={form.logo_url}
                  onChange={e => set('logo_url', e.target.value)}
                  placeholder="https://..."
                />
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
